// frontend/src/components/SpotifyPicker.js

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import { X } from "phosphor-react";
import { api } from "../api";

export default function SpotifyPicker({
  value,
  onChange,
}) {
  const [mode, setMode] = useState(
    value?.type === "playlist"
      ? "playlist"
      : "track"
  );

  const [query, setQuery] = useState("");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [savedTrack, setSavedTrack] = useState(
    value?.type === "track" ? value : null
  );

  const wrapperRef = useRef(null);

  useEffect(() => {
    if (mode !== "track") return;

    const cleaned = query.trim();

    if (!cleaned) {
      setTracks([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const token =
          localStorage.getItem("token") ||
          sessionStorage.getItem("token");

        const response = await api.get(
          "/music/spotify/search",
          {
            params: {
              q: cleaned,
            },
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : undefined,
            signal: controller.signal,
          }
        );

        setTracks(
          Array.isArray(response.data?.tracks)
            ? response.data.tracks
            : []
        );
      } catch (error) {
        if (
          error?.name !== "CanceledError" &&
          error?.code !== "ERR_CANCELED"
        ) {
          setTracks([]);
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mode, query]);

  const selectedTrack =
    value?.type === "track" ? value : savedTrack;

  const clear = () => {
    setQuery("");
    setTracks([]);
    setSavedTrack(null);

    onChange({
      type: "none",
      title: "",
      artist: "",
      url: "",
      spotify_id: "",
    });
  };

  return (
    <div ref={wrapperRef}>
      <div className="mb-2 ml-2 flex gap-2">
        <button
          type="button"
          onClick={() => {
            setMode("track");
            setQuery("");
            setTracks([]);
          }}
          className={[
            "rounded-full !min-h-0 !min-w-0 px-3 py-1 text-xs font-semibold",
            mode === "track"
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-600",
          ].join(" ")}
        >
          Song
        </button>

        <button
          type="button"
          onClick={() => {
            setMode("playlist");
            setQuery("");
            setTracks([]);
          }}
          className={[
            "rounded-full !min-h-0 !min-w-0 px-3 py-1 text-xs font-semibold",
            mode === "playlist"
              ? "bg-blue-100 text-blue-700"
              : "bg-gray-100 text-gray-600",
          ].join(" ")}
        >
          Playlist
        </button>
      </div>

      {mode === "track" ? (
        <>
          {selectedTrack?.title ? (
            <div
              className="
                flex w-full min-w-0 items-center justify-between
                rounded-2xl border border-gray-200
                !min-h-0 !min-w-0 px-3 py-1.5
              "
            >
              <div className="min-w-0 flex-1">
                <p className="whitespace-normal break-words text-xs font-semibold leading-tight text-gray-900">
                  {selectedTrack.title}
                </p>

                {selectedTrack.artist && (
                  <p className="whitespace-normal break-words text-[11px] leading-tight text-gray-500">
                    {selectedTrack.artist}
                  </p>
                )}
              </div>

              <button
                type="button"
                onClick={clear}
                className="
                  inline-flex
                  shrink-0 items-start justify-center
                "
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(event) =>
                  setQuery(event.target.value)
                }
                placeholder="Search Spotify"
                className="
                  w-full rounded-2xl
                  border border-gray-200
                  !min-h-0 !min-w-0 px-3 py-1.5 text-xs
                  outline-none
                  focus:border-2
                  focus:border-blue-500
                "
              />

              {!!query.trim() && (
                <div
                  className="
                    absolute left-0 right-0 z-40
                    mt-1 max-h-64 overflow-y-auto
                    rounded-2xl border
                    bg-white p-1 shadow-lg
                  "
                >
                  {loading ? (
                    <p className="px-3 py-1 !min-h-0 !min-w-0 text-xs text-gray-500">
                      Searching Spotify…
                    </p>
                  ) : tracks.length > 0 ? (
                    tracks.map((track) => (
                      <button
                        key={track.spotify_id}
                        type="button"
                        onClick={() => {
                          const nextTrack = {
                            type: "track",
                            title: track.title || "",
                            artist: track.artist || "",
                            url: track.url || "",
                            spotify_id: track.spotify_id || "",
                          };

                          setSavedTrack(nextTrack);
                          onChange(nextTrack);

                          setQuery("");
                          setTracks([]);
                        }}
                        className="
                          block w-full rounded-xl
                          !min-h-0 !min-w-0 px-3 py-1 text-left
                          hover:bg-blue-50
                        "
                      >
                        <p className="text-sm font-semibold text-gray-900">
                          {track.title}
                        </p>

                        <p className="text-xs text-gray-500">
                          {track.artist}
                        </p>
                      </button>
                    ))
                  ) : (
                    <p className="!min-h-0 !min-w-0 px-3 py-1 text-xs text-gray-500">
                      No songs found
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="relative">
          <input
            type="url"
            value={
              value?.type === "playlist"
                ? value.url || ""
                : ""
            }
            onChange={(event) => {
              onChange({
                type: "playlist",
                title: "",
                artist: "",
                url: event.target.value,
                spotify_id: "",
              });
            }}
            onBlur={async (event) => {
              const url = event.target.value.trim();

              if (!url) return;

              try {
                const token =
                  localStorage.getItem("token") ||
                  sessionStorage.getItem("token");

                const response = await api.get(
                  "/music/spotify/playlist",
                  {
                    params: {
                      url,
                    },
                    headers: token
                      ? {
                          Authorization: `Bearer ${token}`,
                        }
                      : undefined,
                  }
                );

                console.log(
                  "PLAYLIST API RESPONSE:",
                  response.data
                );

                const playlist = response.data?.playlist;

                console.log(
                  "PLAYLIST OBJECT:",
                  playlist
                );

                console.log(
                  "PLAYLIST TITLE:",
                  playlist?.title
                );

                if (!playlist) return;

                onChange({
                  type: "playlist",
                  title: playlist.title || "",
                  artist: playlist.owner || "",
                  url: playlist.url || url,
                  spotify_id: playlist.spotify_id || "",
                });
              } catch (error) {
                // Leave the URL in place if metadata
                // lookup fails.
                  console.error(
                    "PLAYLIST LOOKUP FAILED:",
                    error
                  );

                  console.error(
                    "SERVER RESPONSE:",
                    error?.response?.data
                  );
              }
            }}
            placeholder="Paste Spotify playlist link"
            className="
              w-full rounded-2xl
              border border-gray-200
              !min-h-0 !min-w-0 px-3 py-1.5 text-xs
              outline-none
              focus:border-2
              focus:border-blue-500
            "
          />

          {value?.type === "playlist" &&
            value?.title && (
              <p className="mt-1 px-1 text-[11px] text-gray-500">
                {value.title}
              </p>
            )}
        </div>
      )}
    </div>
  );
}